import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import { extend } from '@react-three/fiber';
import { Geometry, Base, Subtraction } from '@react-three/csg';
import { useSelector } from 'react-redux';

import WallShaderMaterial from '../../materials/WallShaderMaterial';
import { cloneAndScaleUV, setupTextureRepeat } from '../../utils/geometryUltis';
import Plyta from './Yta';

extend({ WallShaderMaterial });

// ----------------- HELPERY -----------------

const getRoofingModelPath = (activeConstruction) => {
  switch (activeConstruction) {
    case 1:
      return '/models/garages/spad-tyl-v3.glb';
    case 2:
      return '/models/garages/dwuspadowy.glb';
    case 3:
      return '/models/garages/spad-w-lewo.glb';
    case 4:
      return '/models/garages/spad-w-prawo.glb';
    case 5:
      return '/models/garages/spad-w-przod.glb';
    case 6:
      return '/models/garages/dwuspadowy-na-brame.glb';
    default:
      return '/models/garages/spad-tyl-v3.glb';
  }
};

const getExtensionCmFromSize = (size) => {
  const n = Number(size) || 0;
  if (n <= 0) return 0;

  if (n >= 20) {
    const steps = n - 20;
    if (steps <= 0) return 0;
    return steps * 50;
  }

  if (n <= 1) return 0;
  return (n - 1) * 50;
};

const getPlacementSide = (placement) => {
  if (placement === 'left' || placement === 1 || placement === '1') return 'left';
  if (placement === 'right' || placement === 2 || placement === '2') return 'right';
  return 'left';
};

const WALL_TYPE_NONE = 3;

/* =========================================================================
 *  ROOFING BASE – stary typ (case 1,5,6)
 *  (zostawiamy bez zmian)
 * ========================================================================= */

function RoofingBase({
                       activeConstruction,
                       textures,
                       length,
                       width,
                       height,
                       position = [0, 0, 0],
                       roofingExtension,
                       extensionCm,
                       ...rest
                     }) {
  const side = getPlacementSide(roofingExtension?.placement);
  const isLeftPlacement = side === 'left';

  const wall_left_type = roofingExtension?.wall_left_type ?? null;
  const wall_front_type = roofingExtension?.wall_front_type ?? null;
  const wall_right_type = roofingExtension?.wall_right_type ?? null;

  // wymiary w cm
  const baseLengthCm = length;
  const baseWidthCm = width;
  const baseHeightCm = height;

  const totalRoofWidthCm = baseWidthCm + 2 * extensionCm;

  // wymiary w m
  const baseLengthM = baseLengthCm / 100;
  const baseHeightM = baseHeightCm / 100;
  const baseWidthM = baseWidthCm / 100;
  const totalRoofWidthM = totalRoofWidthCm / 100;
  const extensionM = extensionCm / 100;

  const modelPath = useMemo(
    () => getRoofingModelPath(activeConstruction),
    [activeConstruction]
  );

  const { nodes, materials } = useGLTF(modelPath);

  // ---------- GEOMETRIE ----------

  const scaledRoofGeometry = useMemo(() => {
    if (!nodes?.Celing?.geometry) return null;
    const g = nodes.Celing.geometry.clone();
    return cloneAndScaleUV(
      g,
      baseLengthM,
      baseHeightM,
      totalRoofWidthM,
      baseLengthM,
      totalRoofWidthM
    );
  }, [nodes?.Celing?.geometry, baseLengthM, baseHeightM, totalRoofWidthM]);

  const scaledLeftGeometry = useMemo(() => {
    if (!nodes?.Left_side?.geometry) return null;
    const g = nodes.Left_side.geometry.clone();
    return cloneAndScaleUV(
      g,
      baseLengthM,
      baseHeightM,
      totalRoofWidthM,
      baseLengthM,
      baseHeightM
    );
  }, [nodes?.Left_side?.geometry, baseLengthM, baseHeightM, totalRoofWidthM]);

  const scaledRightGeometry = useMemo(() => {
    if (!nodes?.Right_side?.geometry) return null;
    const g = nodes.Right_side.geometry.clone();
    return cloneAndScaleUV(
      g,
      baseLengthM,
      baseHeightM,
      totalRoofWidthM,
      baseLengthM,
      baseHeightM
    );
  }, [nodes?.Right_side?.geometry, baseLengthM, baseHeightM, totalRoofWidthM]);

  const scaledFrontGeometry = useMemo(() => {
    if (!nodes?.Front_side?.geometry) return null;
    const g = nodes.Front_side.geometry.clone();
    return cloneAndScaleUV(
      g,
      baseLengthM,
      baseHeightM,
      totalRoofWidthM,
      totalRoofWidthM,
      baseHeightM
    );
  }, [nodes?.Front_side?.geometry, baseLengthM, baseHeightM, totalRoofWidthM]);

  const scaledBackGeometry = useMemo(() => {
    if (!nodes?.Back_side?.geometry) return null;
    const g = nodes.Back_side.geometry.clone();
    return cloneAndScaleUV(
      g,
      baseLengthM,
      baseHeightM,
      totalRoofWidthM,
      totalRoofWidthM,
      baseHeightM
    );
  }, [nodes?.Back_side?.geometry, baseLengthM, baseHeightM, totalRoofWidthM]);

  const leftWallZ = -baseWidthM / 2;
  const rightWallZ = baseWidthM / 2;

  const cutWidthM = totalRoofWidthM * 2;

  const cutBoxGeometry = useMemo(() => {
    if (!scaledRoofGeometry || extensionCm <= 0) return null;
    const cutLengthM = baseLengthM * 1.5;
    const cutHeightM = (baseHeightM || 1) * 4;
    return new THREE.BoxGeometry(cutLengthM, cutHeightM, cutWidthM);
  }, [scaledRoofGeometry, extensionCm, baseLengthM, baseHeightM, cutWidthM]);

  const cutCenterZ = useMemo(() => {
    const halfCut = cutWidthM / 2;
    if (extensionCm <= 0) return 0;
    if (side === 'left') return leftWallZ + halfCut;
    if (side === 'right') return rightWallZ - halfCut;
    return leftWallZ + halfCut;
  }, [side, extensionCm, cutWidthM, leftWallZ, rightWallZ]);

  // ---------- MATERIAŁY ----------

  const RoofMaterial = useMemo(() => {
    if (!materials || !textures?.roof_color) return null;
    const mat = materials.Material.clone();
    const roofColor = textures.roof_color;

    // dach zadaszenia widoczny z góry i z dołu
    mat.side = THREE.DoubleSide;
    mat.polygonOffset = true;
    mat.polygonOffsetFactor = -1;
    mat.polygonOffsetUnits = -1;

    const diff = roofColor.textureDiff?.clone();
    const metal = roofColor.textureMetalness?.clone();
    const normal = roofColor.textureNormalness?.clone();

    const repeatU = 1 / roofColor.multiplierHorizontal;
    const repeatVBase = 1 / roofColor.multiplierVertical;
    const widthRatio = totalRoofWidthM / baseWidthM;
    const repeatV = repeatVBase * widthRatio;

    if (diff) {
      setupTextureRepeat(diff, repeatU, repeatV);
      mat.map = diff;
    }
    if (metal) {
      setupTextureRepeat(metal, repeatU, repeatV);
      mat.metalnessMap = metal;
    }
    if (normal) {
      setupTextureRepeat(normal, repeatU, repeatV);
      mat.normalMap = normal;
    }

    mat.metalness = roofColor.metalnessValue;
    mat.roughness = roofColor.rougnessValue;
    mat.color = new THREE.Color(roofColor.hex);
    mat.needsUpdate = true;

    return mat;
  }, [materials, textures?.roof_color, baseWidthM, totalRoofWidthM]);

  const SideWallMaterial = useMemo(() => {
    if (!materials || !textures?.walls_color) return null;

    const mat = materials.Material.clone();
    mat.side = THREE.DoubleSide;

    const wallColor = textures.walls_color;

    const diff = wallColor.textureDiff?.clone();
    const metal = wallColor.textureMetalness?.clone();
    const normal = wallColor.textureNormalness?.clone();

    if (diff) {
      setupTextureRepeat(
        diff,
        baseLengthM / wallColor.multiplierHorizontal,
        1 / wallColor.multiplierVertical
      );
      mat.map = diff;
    }
    if (metal) {
      setupTextureRepeat(
        metal,
        baseLengthM / wallColor.multiplierHorizontal,
        1 / wallColor.multiplierVertical
      );
      mat.metalnessMap = metal;
    }
    if (normal) {
      setupTextureRepeat(
        normal,
        baseLengthM / wallColor.multiplierHorizontal,
        1 / wallColor.multiplierVertical
      );
      mat.normalMap = normal;
    }

    mat.metalness = wallColor.metalnessValue;
    mat.roughness = wallColor.roughnessValue;
    mat.color = new THREE.Color(wallColor.hex);
    mat.needsUpdate = true;

    return mat;
  }, [materials, textures?.walls_color, baseLengthM]);

  const FrontBackWallMaterial = useMemo(() => {
    if (!materials || !textures?.walls_color) return null;

    const mat = materials.Material.clone();
    mat.side = THREE.DoubleSide;

    const wallColor = textures.walls_color;

    const diff = wallColor.textureDiff?.clone();
    const metal = wallColor.textureMetalness?.clone();
    const normal = wallColor.textureNormalness?.clone();

    const repeatU = 1 / wallColor.multiplierHorizontal;
    const repeatV = 1 / wallColor.multiplierVertical;

    if (diff) {
      setupTextureRepeat(diff, repeatU, repeatV);
      mat.map = diff;
    }
    if (metal) {
      setupTextureRepeat(metal, repeatU, repeatV);
      mat.metalnessMap = metal;
    }
    if (normal) {
      setupTextureRepeat(normal, repeatU, repeatV);
      mat.normalMap = normal;
    }

    mat.metalness = wallColor.metalnessValue;
    mat.roughness = wallColor.roughnessValue;
    mat.color = new THREE.Color(wallColor.hex);
    mat.needsUpdate = true;

    return mat;
  }, [materials, textures?.walls_color]);

  const SideWallMaterialType2 = useMemo(() => {
    if (!materials || !textures?.walls_color) return null;

    const mat = materials.Material.clone();
    mat.side = THREE.DoubleSide;

    const wallColor = textures.walls_color;
    const diff = wallColor.textureDiff?.clone();

    if (diff) {
      setupTextureRepeat(
        diff,
        baseLengthM / wallColor.multiplierHorizontal,
        1 / wallColor.multiplierVertical
      );
      mat.map = diff;
    }

    mat.normalMap = null;
    mat.metalnessMap = null;
    mat.metalness = 0;
    mat.roughness = 1;
    mat.color = new THREE.Color(wallColor.hex);
    mat.needsUpdate = true;

    return mat;
  }, [materials, textures?.walls_color, baseLengthM]);

  const FrontBackWallMaterialType2 = useMemo(() => {
    if (!materials || !textures?.walls_color) return null;

    const mat = materials.Material.clone();
    mat.side = THREE.DoubleSide;

    const wallColor = textures.walls_color;
    const diff = wallColor.textureDiff?.clone();

    if (diff) {
      setupTextureRepeat(
        diff,
        1 / wallColor.multiplierHorizontal,
        1 / wallColor.multiplierVertical
      );
      mat.map = diff;
    }

    mat.normalMap = null;
    mat.metalnessMap = null;
    mat.metalness = 0;
    mat.roughness = 1;
    mat.color = new THREE.Color(wallColor.hex);
    mat.needsUpdate = true;

    return mat;
  }, [materials, textures?.walls_color]);

  const PillarMaterial = useMemo(() => {
    if (!materials || !textures?.walls_color) return null;
    const mat = materials.Material.clone();
    const wallColor = textures.walls_color;
    mat.side = THREE.FrontSide;
    mat.color = new THREE.Color(wallColor.hex);
    mat.metalness = wallColor.metalnessValue;
    mat.roughness = wallColor.roughnessValue;
    mat.needsUpdate = true;
    return mat;
  }, [materials, textures?.walls_color]);

  // ---------- LAMELE (wycięcia) ----------

  const slatSubtractions = useMemo(() => {
    if (!cutBoxGeometry) return [];

    const result = [];

    const holeHeight = 0.05; // 5 cm
    const solidHeight = 0.20; // 20 cm
    const step = holeHeight + solidHeight;

    const H = baseHeightM;
    if (H <= holeHeight) return [];

    const stripeGeom = new THREE.BoxGeometry(
      baseLengthM * 3,
      holeHeight,
      totalRoofWidthM * 3
    );

    const stripes = [];
    let y = holeHeight / 2;

    while (y + holeHeight / 2 < H) {
      stripes.push({ center: y });
      y += step;
    }

    if (stripes.length === 0) return [];

    const last = stripes[stripes.length - 1];
    const lastTop = last.center + holeHeight / 2;
    const leftoverTopSolid = H - lastTop;

    if (leftoverTopSolid < solidHeight) {
      const extendedHeight = holeHeight + leftoverTopSolid;
      const extendedGeom = new THREE.BoxGeometry(
        baseLengthM * 3,
        extendedHeight,
        totalRoofWidthM * 3
      );
      const extendedCenter = last.center + leftoverTopSolid / 2;

      for (let i = 0; i < stripes.length - 1; i++) {
        result.push({
          geometry: stripeGeom,
          position: [0, stripes[i].center, 0],
        });
      }

      result.push({
        geometry: extendedGeom,
        position: [0, extendedCenter, 0],
      });
    } else {
      stripes.forEach((s) => {
        result.push({
          geometry: stripeGeom,
          position: [0, s.center, 0],
        });
      });
    }

    return result;
  }, [cutBoxGeometry, baseHeightM, baseLengthM, totalRoofWidthM]);

  // ---------- SŁUPKI PRZYCIĘTE DO DACHU ----------

  const pillarSizeM = 0.10;
  const pillarHalf = pillarSizeM / 2;
  const pillarOffsetOutM = 0.03;

  const halfLength = baseLengthM / 2;
  const innerFrontX = halfLength - pillarHalf;
  const innerBackX = -halfLength + pillarHalf;

  const pillarFrontX = innerFrontX + pillarOffsetOutM;
  const pillarBackX = innerBackX - pillarOffsetOutM;

  const outerEdgeZ = isLeftPlacement
    ? leftWallZ - extensionM
    : rightWallZ + extensionM;

  const innerPillarCenterZ = isLeftPlacement
    ? outerEdgeZ + pillarHalf
    : outerEdgeZ - pillarHalf;

  const pillarCenterZ = isLeftPlacement
    ? innerPillarCenterZ - pillarOffsetOutM
    : innerPillarCenterZ + pillarOffsetOutM;

  const pillarHeights = useMemo(() => {
    if (!scaledRoofGeometry) {
      return { front: baseHeightM, back: baseHeightM };
    }

    const posAttr = scaledRoofGeometry.attributes.position;
    if (!posAttr) {
      return { front: baseHeightM, back: baseHeightM };
    }

    const arr = posAttr.array;
    let xMin = Infinity;
    let xMax = -Infinity;

    for (let i = 0; i < arr.length; i += 3) {
      const x = arr[i];
      if (x < xMin) xMin = x;
      if (x > xMax) xMax = x;
    }

    if (!isFinite(xMin) || !isFinite(xMax) || xMax === xMin) {
      return { front: baseHeightM, back: baseHeightM };
    }

    const eps = (xMax - xMin) * 0.05;
    let lowYSum = 0;
    let lowCount = 0;
    let highYSum = 0;
    let highCount = 0;

    for (let i = 0; i < arr.length; i += 3) {
      const x = arr[i];
      const y = arr[i + 1];

      if (Math.abs(x - xMin) <= eps) {
        lowYSum += y;
        lowCount++;
      }
      if (Math.abs(x - xMax) <= eps) {
        highYSum += y;
        highCount++;
      }
    }

    if (!lowCount || !highCount) {
      return { front: baseHeightM, back: baseHeightM };
    }

    const lowY = lowYSum / lowCount;
    const highY = highYSum / highCount;
    const dx = xMax - xMin || 1e-6;

    const roofYAtX = (x) => {
      const t = (x - xMin) / dx;
      return lowY + t * (highY - lowY);
    };

    const frontRoofY = roofYAtX(pillarFrontX);
    const backRoofY = roofYAtX(pillarBackX);

    return {
      front: Math.max(0, frontRoofY),
      back: Math.max(0, backRoofY),
    };
  }, [scaledRoofGeometry, baseHeightM, pillarFrontX, pillarBackX]);

  const pillarHeightFront = pillarHeights.front;
  const pillarHeightBack = pillarHeights.back;

  const pillarGeometryFront = useMemo(
    () => new THREE.BoxGeometry(pillarSizeM, pillarHeightFront, pillarSizeM),
    [pillarHeightFront]
  );
  const pillarGeometryBack = useMemo(
    () => new THREE.BoxGeometry(pillarSizeM, pillarHeightBack, pillarSizeM),
    [pillarHeightBack]
  );

  const pillarFrontY = pillarHeightFront / 2;
  const pillarBackY = pillarHeightBack / 2;

  // ---------- WARUNEK RENDERU ----------

  const shouldRender =
    extensionCm > 0 && scaledRoofGeometry && cutBoxGeometry && RoofMaterial;

  if (!shouldRender) return null;

  const frontWallGeometry = isLeftPlacement ? scaledLeftGeometry : scaledRightGeometry;
  const leftWallGeometry = isLeftPlacement ? scaledFrontGeometry : scaledBackGeometry;
  const rightWallGeometry = isLeftPlacement ? scaledBackGeometry : scaledFrontGeometry;

  const frontIsType2 = wall_front_type === 2;
  const leftIsType2 = wall_left_type === 2;
  const rightIsType2 = wall_right_type === 2;

  const frontMaterial = frontIsType2 ? SideWallMaterialType2 : SideWallMaterial;
  const leftMaterial = leftIsType2 ? FrontBackWallMaterialType2 : FrontBackWallMaterial;
  const rightMaterial = rightIsType2 ? FrontBackWallMaterialType2 : FrontBackWallMaterial;

  const renderFrontWall =
    frontWallGeometry && wall_front_type !== WALL_TYPE_NONE && frontMaterial;
  const renderLeftWall =
    leftWallGeometry && wall_left_type !== WALL_TYPE_NONE && leftMaterial;
  const renderRightWall =
    rightWallGeometry && wall_right_type !== WALL_TYPE_NONE && rightMaterial;

  const slabCenterZ =
    side === 'left'
      ? leftWallZ - extensionM / 2
      : rightWallZ + extensionM / 2;

  return (
    <group
      {...rest}
      position={position}
      dispose={null}
      castShadow
      receiveShadow
    >
      {/* DACH */}
      <mesh name="Roofing_Roof" material={RoofMaterial}>
        <Geometry>
          <Base geometry={scaledRoofGeometry} />
          <Subtraction geometry={cutBoxGeometry} position={[0, 0, cutCenterZ]} />
        </Geometry>
      </mesh>

      {/* ŚCIANA FRONT (długa) */}
      {renderFrontWall && (
        <mesh name="Roofing_FrontWall" material={frontMaterial}>
          <Geometry>
            <Base geometry={frontWallGeometry} />
            <Subtraction geometry={cutBoxGeometry} position={[0, 0, cutCenterZ]} />
            {frontIsType2 &&
              slatSubtractions.map(({ geometry, position }, i) => (
                <Subtraction
                  key={`front-slat-${i}`}
                  geometry={geometry}
                  position={position}
                />
              ))}
          </Geometry>
        </mesh>
      )}

      {/* ŚCIANA LEWA (krótka) */}
      {renderLeftWall && (
        <mesh name="Roofing_LeftWall" material={leftMaterial}>
          <Geometry>
            <Base geometry={leftWallGeometry} />
            <Subtraction geometry={cutBoxGeometry} position={[0, 0, cutCenterZ]} />
            {leftIsType2 &&
              slatSubtractions.map(({ geometry, position }, i) => (
                <Subtraction
                  key={`left-slat-${i}`}
                  geometry={geometry}
                  position={position}
                />
              ))}
          </Geometry>
        </mesh>
      )}

      {/* ŚCIANA PRAWA (krótka) */}
      {renderRightWall && (
        <mesh name="Roofing_RightWall" material={rightMaterial}>
          <Geometry>
            <Base geometry={rightWallGeometry} />
            <Subtraction geometry={cutBoxGeometry} position={[0, 0, cutCenterZ]} />
            {rightIsType2 &&
              slatSubtractions.map(({ geometry, position }, i) => (
                <Subtraction
                  key={`right-slat-${i}`}
                  geometry={geometry}
                  position={position}
                />
              ))}
          </Geometry>
        </mesh>
      )}

      {/* SŁUPKI */}
      {PillarMaterial && (
        <>
          <mesh
            name="Roofing_PillarFront"
            geometry={pillarGeometryFront}
            material={PillarMaterial}
            position={[pillarFrontX, pillarFrontY, pillarCenterZ]}
            castShadow
            receiveShadow
          />
          <mesh
            name="Roofing_PillarBack"
            geometry={pillarGeometryBack}
            material={PillarMaterial}
            position={[pillarBackX, pillarBackY, pillarCenterZ]}
            castShadow
            receiveShadow
          />
        </>
      )}

      {/* PŁYTA */}
      <Plyta
        length={baseLengthCm}
        width={extensionCm}
        anchoring={roofingExtension?.anchoring}
        position={[0, 0.05, slabCenterZ]}
      />
    </group>
  );
}

/* =========================================================================
 *  ROOFING TYPE 2 – activeConstruction 2/3/4
 *  - działa rozciąganie
 *  - działa wybór strony (left/right) dla 2/3/4
 *  - poprawka: obie krótkie ściany są przesuwane o extensionM (żeby nie znikały)
 *  - CSG: ogromny box tnie "od początku płyty w stronę garażu"
 *  - dodane: lamele (wall type 2) + słupki docinane do połaci
 *  - POPRAWKA: prawidłowe kafelkowanie tekstur (roof + short/long walls)
 * ========================================================================= */

function RoofingType2({
                        activeConstruction,
                        textures,
                        length,
                        width,
                        height,
                        position = [0, 0, 0],
                        roofingExtension,
                        extensionCm,
                        ...rest
                      }) {
  const sideFromConfig = getPlacementSide(roofingExtension?.placement);
  const isCanopyOnLeft = sideFromConfig === 'left';

  const wall_left_type = roofingExtension?.wall_left_type ?? null;
  const wall_front_type = roofingExtension?.wall_front_type ?? null;
  const wall_right_type = roofingExtension?.wall_right_type ?? null;

  const baseLengthCm = length;
  const baseWidthCm = width;
  const baseHeightCm = height;

  const baseLengthM = baseLengthCm / 100;
  const baseHeightM = baseHeightCm / 100;
  const baseWidthM = baseWidthCm / 100;
  const extensionM = extensionCm / 100;

  const leftWallZ = -baseWidthM / 2;
  const rightWallZ = baseWidthM / 2;

  const modelPath = useMemo(
    () => getRoofingModelPath(activeConstruction),
    [activeConstruction]
  );

  const { nodes, materials } = useGLTF(modelPath);

  // --- DACH GARAŻU – baza do wyznaczenia płaszczyzny połaci ---
  const roofBaseGeometry = useMemo(() => {
    if (!nodes?.Celing?.geometry) return null;
    const g = nodes.Celing.geometry.clone();
    return cloneAndScaleUV(
      g,
      baseLengthM,
      baseHeightM,
      baseWidthM,
      baseLengthM,
      baseWidthM
    );
  }, [nodes?.Celing?.geometry, baseLengthM, baseHeightM, baseWidthM]);

  const roofBounds = useMemo(() => {
    if (!roofBaseGeometry?.attributes?.position) return null;
    const arr = roofBaseGeometry.attributes.position.array;

    let xMin = Infinity;
    let xMax = -Infinity;
    let zMin = Infinity;
    let zMax = -Infinity;

    for (let i = 0; i < arr.length; i += 3) {
      const x = arr[i];
      const z = arr[i + 2];
      if (x < xMin) xMin = x;
      if (x > xMax) xMax = x;
      if (z < zMin) zMin = z;
      if (z > zMax) zMax = z;
    }

    if (!isFinite(xMin) || !isFinite(xMax) || !isFinite(zMin) || !isFinite(zMax)) {
      return null;
    }

    return { xMin, xMax, zMin, zMax };
  }, [roofBaseGeometry]);

  const roofLenM = useMemo(() => {
    if (!roofBounds) return baseLengthM;
    return Math.max(0.0001, Math.abs(roofBounds.xMax - roofBounds.xMin));
  }, [roofBounds, baseLengthM]);

  const isGable = activeConstruction === 2;

  // Płaszczyzna połaci:
  // - 2: okap + kalenica
  // - 3/4: regresja liniowa Y = m*Z + b
  const roofPlane = useMemo(() => {
    if (!roofBaseGeometry || !roofBounds) return null;
    const { zMin, zMax } = roofBounds;
    const pos = roofBaseGeometry.attributes.position;
    const arr = pos.array;

    if (isGable) {
      const zEave = isCanopyOnLeft ? zMin : zMax;
      const zCenter = (zMin + zMax) / 2;

      const spanZ = Math.max(zMax - zMin, 1e-6);
      const epsEdge = spanZ * 0.05;
      const epsCenter = spanZ * 0.05;

      let eaveYSum = 0;
      let eaveCount = 0;
      let ridgeYSum = 0;
      let ridgeCount = 0;

      for (let i = 0; i < arr.length; i += 3) {
        const y = arr[i + 1];
        const z = arr[i + 2];

        if (Math.abs(z - zEave) <= epsEdge) {
          eaveYSum += y;
          eaveCount++;
        }

        if (Math.abs(z - zCenter) <= epsCenter) {
          ridgeYSum += y;
          ridgeCount++;
        }
      }

      if (!eaveCount || !ridgeCount) return null;

      const eaveY = eaveYSum / eaveCount;
      const ridgeY = ridgeYSum / ridgeCount;

      const run = zCenter - zEave || 1e-6;
      const slope = (ridgeY - eaveY) / run;

      const roofYAtZ = (z) => eaveY + slope * (z - zEave);

      return { zEave, roofYAtZ };
    }

    // SPAD W LEWO / PRAWO
    let sumZ = 0;
    let sumY = 0;
    let sumZ2 = 0;
    let sumZY = 0;
    let count = 0;

    for (let i = 0; i < arr.length; i += 3) {
      const y = arr[i + 1];
      const z = arr[i + 2];
      sumZ += z;
      sumY += y;
      sumZ2 += z * z;
      sumZY += z * y;
      count++;
    }

    if (!count) return null;

    const denom = count * sumZ2 - sumZ * sumZ || 1e-6;
    const m = (count * sumZY - sumZ * sumY) / denom;
    const b = (sumY - m * sumZ) / count;

    const roofYAtZ = (z) => m * z + b;
    const zEave = isCanopyOnLeft ? zMin : zMax;

    return { zEave, roofYAtZ };
  }, [roofBaseGeometry, roofBounds, isGable, isCanopyOnLeft]);

  // Dach zadaszenia jako plane dopasowany do połaci
  const canopyRoofGeometry = useMemo(() => {
    if (extensionM <= 0) return null;
    if (!roofPlane || !roofBounds) return null;

    const { roofYAtZ, zEave } = roofPlane;
    const { xMin, xMax } = roofBounds;

    const L = xMax - xMin;
    const xCenter = (xMin + xMax) / 2;

    const g = new THREE.PlaneGeometry(L, extensionM, 1, 1);
    const pos = g.attributes.position;
    const arr = pos.array;

    for (let i = 0; i < arr.length; i += 3) {
      const u = arr[i];      // [-L/2, L/2]
      const v = arr[i + 1];  // [-ext/2, ext/2]

      const xWorld = xCenter + u;
      const zWorld = isCanopyOnLeft
        ? zEave - extensionM / 2 + v
        : zEave + extensionM / 2 + v;

      const yWorld = roofYAtZ(zWorld);

      arr[i] = xWorld;
      arr[i + 1] = yWorld;
      arr[i + 2] = zWorld;
    }

    pos.needsUpdate = true;
    g.computeVertexNormals();
    return g;
  }, [roofPlane, roofBounds, extensionM, isCanopyOnLeft]);

  // --- ŚCIANY – bierzemy z GLB, skalujemy (wysokość x2), potem przycinamy do połaci ---
  const baseLeftWallGeom = useMemo(() => {
    if (!nodes?.Left_side?.geometry) return null;
    const g = nodes.Left_side.geometry.clone();
    return cloneAndScaleUV(
      g,
      baseLengthM,
      baseHeightM * 2,
      baseWidthM,
      baseLengthM,
      baseHeightM * 2
    );
  }, [nodes?.Left_side?.geometry, baseLengthM, baseHeightM, baseWidthM]);

  const baseRightWallGeom = useMemo(() => {
    if (!nodes?.Right_side?.geometry) return null;
    const g = nodes.Right_side.geometry.clone();
    return cloneAndScaleUV(
      g,
      baseLengthM,
      baseHeightM * 2,
      baseWidthM,
      baseLengthM,
      baseHeightM * 2
    );
  }, [nodes?.Right_side?.geometry, baseLengthM, baseHeightM, baseWidthM]);

  const baseFrontGeom = useMemo(() => {
    if (!nodes?.Front_side?.geometry) return null;
    const g = nodes.Front_side.geometry.clone();
    return cloneAndScaleUV(
      g,
      baseLengthM,
      baseHeightM * 2,
      baseWidthM,
      baseWidthM,
      baseHeightM * 2
    );
  }, [nodes?.Front_side?.geometry, baseLengthM, baseHeightM, baseWidthM]);

  const baseBackGeom = useMemo(() => {
    if (!nodes?.Back_side?.geometry) return null;
    const g = nodes.Back_side.geometry.clone();
    return cloneAndScaleUV(
      g,
      baseLengthM,
      baseHeightM * 2,
      baseWidthM,
      baseWidthM,
      baseHeightM * 2
    );
  }, [nodes?.Back_side?.geometry, baseLengthM, baseHeightM, baseWidthM]);

  const clipGeomToRoof = useMemo(() => {
    if (!roofPlane) return (geom) => geom;
    const { roofYAtZ } = roofPlane;

    return (geom) => {
      if (!geom?.attributes?.position) return geom;
      const pos = geom.attributes.position;
      const arr = pos.array;

      for (let i = 0; i < arr.length; i += 3) {
        const y = arr[i + 1];
        const z = arr[i + 2];
        const roofY = roofYAtZ(z);
        if (y > roofY) arr[i + 1] = roofY;
      }

      pos.needsUpdate = true;
      geom.computeVertexNormals();
      return geom;
    };
  }, [roofPlane]);

  // przesunięcie wszystkich ścian zadaszenia o extensionM w stronę zadaszenia
  const shiftZOut = isCanopyOnLeft ? -extensionM : extensionM;

  // DŁUGA ŚCIANA (po zewnętrznej)
  const frontWallGeometry = useMemo(() => {
    if (!baseLeftWallGeom || !baseRightWallGeom) return null;
    const baseGeom = isCanopyOnLeft ? baseLeftWallGeom : baseRightWallGeom;
    const g = baseGeom.clone();
    g.translate(0, 0, shiftZOut);
    return clipGeomToRoof(g);
  }, [baseLeftWallGeom, baseRightWallGeom, isCanopyOnLeft, shiftZOut, clipGeomToRoof]);

  // KRÓTKIE ŚCIANY – obie przesunięte o shiftZOut
  const leftShortWallGeom = useMemo(() => {
    if (!baseFrontGeom || !baseBackGeom) return null;
    const baseGeom = isCanopyOnLeft ? baseBackGeom : baseFrontGeom;
    const g = baseGeom.clone();
    g.translate(0, 0, shiftZOut);
    return clipGeomToRoof(g);
  }, [baseFrontGeom, baseBackGeom, isCanopyOnLeft, shiftZOut, clipGeomToRoof]);

  const rightShortWallGeom = useMemo(() => {
    if (!baseFrontGeom || !baseBackGeom) return null;
    const baseGeom = isCanopyOnLeft ? baseFrontGeom : baseBackGeom;
    const g = baseGeom.clone();
    g.translate(0, 0, shiftZOut);
    return clipGeomToRoof(g);
  }, [baseFrontGeom, baseBackGeom, isCanopyOnLeft, shiftZOut, clipGeomToRoof]);

  // ---------- OGROMNY BOX DO WYCIĘCIA “OD STARTU PŁYTY W STRONĘ GARAŻU” ----------
  const innerCutZ = isCanopyOnLeft ? leftWallZ : rightWallZ;

  const garageCutBoxGeometry = useMemo(() => {
    if (extensionM <= 0) return null;
    const cutLengthX = baseLengthM * 10 + 10;
    const cutHeightY = baseHeightM * 10 + 10;
    const cutDepthZ = (baseWidthM + extensionM) * 10 + 10;
    return new THREE.BoxGeometry(cutLengthX, cutHeightY, cutDepthZ);
  }, [extensionM, baseLengthM, baseHeightM, baseWidthM]);

  const garageCutCenter = useMemo(() => {
    if (!garageCutBoxGeometry) return [0, 0, 0];
    const depth = garageCutBoxGeometry.parameters.depth;
    const half = depth / 2;
    const eps = 0.001;

    const z = isCanopyOnLeft
      ? innerCutZ + half + eps
      : innerCutZ - half - eps;

    return [0, baseHeightM, z];
  }, [garageCutBoxGeometry, innerCutZ, isCanopyOnLeft, baseHeightM]);

  // ---------- LAMELE (gdy wall_*_type === 2) ----------
  const slatSubtractions = useMemo(() => {
    if (!garageCutBoxGeometry) return [];

    const result = [];
    const holeHeight = 0.05;
    const solidHeight = 0.20;
    const step = holeHeight + solidHeight;

    const H = baseHeightM;
    if (H <= holeHeight) return [];

    const stripeGeom = new THREE.BoxGeometry(
      baseLengthM * 12 + 12,
      holeHeight,
      (baseWidthM + extensionM) * 12 + 12
    );

    let y = holeHeight / 2;
    while (y + holeHeight / 2 < H) {
      result.push({ geometry: stripeGeom, position: [0, y, 0] });
      y += step;
    }

    if (result.length) {
      const lastY = result[result.length - 1].position[1];
      const lastTop = lastY + holeHeight / 2;
      const leftover = H - lastTop;

      if (leftover > 0 && leftover < solidHeight) {
        const extendedHeight = holeHeight + leftover;
        const extendedGeom = new THREE.BoxGeometry(
          baseLengthM * 12 + 12,
          extendedHeight,
          (baseWidthM + extensionM) * 12 + 12
        );
        result[result.length - 1] = {
          geometry: extendedGeom,
          position: [0, lastY + leftover / 2, 0],
        };
      }
    }

    return result;
  }, [garageCutBoxGeometry, baseHeightM, baseLengthM, baseWidthM, extensionM]);

  // ---------- SŁUPKI (docinane do połaci) ----------
  const PillarMaterial = useMemo(() => {
    if (!materials || !textures?.walls_color) return null;
    const mat = materials.Material.clone();
    const wallColor = textures.walls_color;
    mat.side = THREE.FrontSide;
    mat.color = new THREE.Color(wallColor.hex);
    mat.metalness = wallColor.metalnessValue;
    mat.roughness = wallColor.roughnessValue;
    mat.needsUpdate = true;
    return mat;
  }, [materials, textures?.walls_color]);

  const showPillars = roofingExtension?.show_pillars ?? true;

  const pillarSizeM = 0.10;
  const pillarHalf = pillarSizeM / 2;
  const pillarOffsetOutM = 0.03;

  const halfLength = baseLengthM / 2;
  const innerFrontX = halfLength - pillarHalf;
  const innerBackX = -halfLength + pillarHalf;

  const pillarFrontX = innerFrontX + pillarOffsetOutM;
  const pillarBackX = innerBackX - pillarOffsetOutM;

  const outerEdgeZ = isCanopyOnLeft
    ? leftWallZ - extensionM
    : rightWallZ + extensionM;

  const pillarCenterZ = isCanopyOnLeft
    ? outerEdgeZ + pillarHalf - pillarOffsetOutM
    : outerEdgeZ - pillarHalf + pillarOffsetOutM;

  const pillarHeights = useMemo(() => {
    const fallback = { front: baseHeightM, back: baseHeightM };
    if (!roofPlane?.roofYAtZ) return fallback;

    const roofYAtZ = roofPlane.roofYAtZ;
    const h = Math.max(0, roofYAtZ(pillarCenterZ));

    return { front: h, back: h };
  }, [roofPlane, pillarCenterZ, baseHeightM]);

  const pillarGeometryFront = useMemo(() => {
    const h = Math.max(0.01, pillarHeights.front);
    return new THREE.BoxGeometry(pillarSizeM, h, pillarSizeM);
  }, [pillarHeights.front]);

  const pillarGeometryBack = useMemo(() => {
    const h = Math.max(0.01, pillarHeights.back);
    return new THREE.BoxGeometry(pillarSizeM, h, pillarSizeM);
  }, [pillarHeights.back]);

  const pillarFrontY = Math.max(0.01, pillarHeights.front) / 2;
  const pillarBackY = Math.max(0.01, pillarHeights.back) / 2;

  // ======================================================================
  // MATERIAŁY + POWTARZANIE TEKSTUR (TO JEST KLUCZOWA POPRAWKA)
  // ======================================================================

  const RoofMaterial = useMemo(() => {
    if (!materials || !textures?.roof_color) return null;
    const roofColor = textures.roof_color;
    const mat = materials.Material.clone();

    mat.side = THREE.DoubleSide;
    mat.polygonOffset = true;
    mat.polygonOffsetFactor = -1;
    mat.polygonOffsetUnits = -1;

    const diff = roofColor.textureDiff?.clone();
    const metal = roofColor.textureMetalness?.clone();
    const normal = roofColor.textureNormalness?.clone();

    // PlaneGeometry ma UV 0..1, więc repeat liczymy “na metry”
    const repeatU = roofLenM / (roofColor.multiplierHorizontal || 1);
    const repeatV = extensionM / (roofColor.multiplierVertical || 1);

    if (diff) {
      setupTextureRepeat(diff, repeatU, repeatV);
      mat.map = diff;
    }
    if (metal) {
      setupTextureRepeat(metal, repeatU, repeatV);
      mat.metalnessMap = metal;
    }
    if (normal) {
      setupTextureRepeat(normal, repeatU, repeatV);
      mat.normalMap = normal;
    }

    mat.metalness = roofColor.metalnessValue;
    mat.roughness = roofColor.rougnessValue;
    mat.color = new THREE.Color(roofColor.hex);
    mat.needsUpdate = true;

    return mat;
  }, [materials, textures?.roof_color, roofLenM, extensionM]);

  // helper: pełny materiał ścian (diff + metal + normal) z kontrolą repeatU
  const makeWallMat = (repeatU, type2 = false) => {
    if (!materials || !textures?.walls_color) return null;

    const wallColor = textures.walls_color;
    const mat = materials.Material.clone();
    mat.side = THREE.DoubleSide;

    const diff = wallColor.textureDiff?.clone();
    const metal = wallColor.textureMetalness?.clone();
    const normal = wallColor.textureNormalness?.clone();

    const repeatV = 1 / (wallColor.multiplierVertical || 1);

    if (diff) {
      setupTextureRepeat(diff, repeatU, repeatV);
      mat.map = diff;
    }

    if (type2) {
      mat.normalMap = null;
      mat.metalnessMap = null;
      mat.metalness = 0;
      mat.roughness = 1;
    } else {
      if (metal) {
        setupTextureRepeat(metal, repeatU, repeatV);
        mat.metalnessMap = metal;
      }
      if (normal) {
        setupTextureRepeat(normal, repeatU, repeatV);
        mat.normalMap = normal;
      }
      mat.metalness = wallColor.metalnessValue;
      mat.roughness = wallColor.roughnessValue;
    }

    mat.color = new THREE.Color(wallColor.hex);
    mat.needsUpdate = true;
    return mat;
  };

  // DŁUGA ściana (po długości garażu) – repeat od długości połaci
  const LongWallMaterial = useMemo(() => {
    if (!textures?.walls_color) return null;
    const wallColor = textures.walls_color;
    const repeatU = roofLenM / (wallColor.multiplierHorizontal || 1);
    return makeWallMat(repeatU, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materials, textures?.walls_color, roofLenM]);

  const LongWallMaterialType2 = useMemo(() => {
    if (!textures?.walls_color) return null;
    const wallColor = textures.walls_color;
    const repeatU = roofLenM / (wallColor.multiplierHorizontal || 1);
    return makeWallMat(repeatU, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materials, textures?.walls_color, roofLenM]);

  // KRÓTKIE ściany (po “głębokości” zadaszenia) – repeat od extensionM (po CSG zostaje tylko extension)
  const ShortWallMaterial = useMemo(() => {
    if (!textures?.walls_color) return null;
    const wallColor = textures.walls_color;

    // KRÓTKIE ŚCIANY mają UV już w “skali garażu” (cloneAndScaleUV),
    // więc repeat nie może zależeć od extensionM
    const repeatU = 1 / (wallColor.multiplierHorizontal || 1);
    return makeWallMat(repeatU, false);
  }, [materials, textures?.walls_color]);

  const ShortWallMaterialType2 = useMemo(() => {
    if (!textures?.walls_color) return null;
    const wallColor = textures.walls_color;

    const repeatU = 1 / (wallColor.multiplierHorizontal || 1);
    return makeWallMat(repeatU, true);
  }, [materials, textures?.walls_color]);


  const shouldRender = extensionCm > 0 && canopyRoofGeometry && RoofMaterial;
  if (!shouldRender) return null;

  const frontIsType2 = wall_front_type === 2;
  const leftIsType2 = wall_left_type === 2;
  const rightIsType2 = wall_right_type === 2;

  const frontMat = frontIsType2 ? LongWallMaterialType2 : LongWallMaterial;
  const leftMat = leftIsType2 ? ShortWallMaterialType2 : ShortWallMaterial;
  const rightMat = rightIsType2 ? ShortWallMaterialType2 : ShortWallMaterial;

  const renderFrontWall =
    frontWallGeometry && wall_front_type !== WALL_TYPE_NONE && frontMat;
  const renderLeftWall =
    leftShortWallGeom && wall_left_type !== WALL_TYPE_NONE && leftMat;
  const renderRightWall =
    rightShortWallGeom && wall_right_type !== WALL_TYPE_NONE && rightMat;

  const canCut = Boolean(garageCutBoxGeometry);

  const slabCenterZ = isCanopyOnLeft
    ? leftWallZ - extensionM / 2
    : rightWallZ + extensionM / 2;

  return (
    <group
      {...rest}
      position={position}
      dispose={null}
      castShadow
      receiveShadow
    >
      {/* DACH */}
      <mesh
        name="Roofing2_Roof"
        geometry={canopyRoofGeometry}
        material={RoofMaterial}
        castShadow
        receiveShadow
      />

      {/* DŁUGA ŚCIANA */}
      {renderFrontWall && (
        <mesh
          name="Roofing2_FrontWall"
          material={frontMat}
          castShadow
          receiveShadow
        >
          {canCut ? (
            <Geometry>
              <Base geometry={frontWallGeometry} />
              <Subtraction geometry={garageCutBoxGeometry} position={garageCutCenter} />
              {frontIsType2 &&
                slatSubtractions.map(({ geometry, position }, i) => (
                  <Subtraction
                    key={`front-slat-${i}`}
                    geometry={geometry}
                    position={position}
                  />
                ))}
            </Geometry>
          ) : (
            <primitive object={frontWallGeometry} attach="geometry" />
          )}
        </mesh>
      )}

      {/* KRÓTKA ŚCIANA 1 */}
      {renderLeftWall && (
        <mesh
          name="Roofing2_LeftWall"
          material={leftMat}
          castShadow
          receiveShadow
        >
          {canCut ? (
            <Geometry>
              <Base geometry={leftShortWallGeom} />
              <Subtraction geometry={garageCutBoxGeometry} position={garageCutCenter} />
              {leftIsType2 &&
                slatSubtractions.map(({ geometry, position }, i) => (
                  <Subtraction
                    key={`left-slat-${i}`}
                    geometry={geometry}
                    position={position}
                  />
                ))}
            </Geometry>
          ) : (
            <primitive object={leftShortWallGeom} attach="geometry" />
          )}
        </mesh>
      )}

      {/* KRÓTKA ŚCIANA 2 */}
      {renderRightWall && (
        <mesh
          name="Roofing2_RightWall"
          material={rightMat}
          castShadow
          receiveShadow
        >
          {canCut ? (
            <Geometry>
              <Base geometry={rightShortWallGeom} />
              <Subtraction geometry={garageCutBoxGeometry} position={garageCutCenter} />
              {rightIsType2 &&
                slatSubtractions.map(({ geometry, position }, i) => (
                  <Subtraction
                    key={`right-slat-${i}`}
                    geometry={geometry}
                    position={position}
                  />
                ))}
            </Geometry>
          ) : (
            <primitive object={rightShortWallGeom} attach="geometry" />
          )}
        </mesh>
      )}

      {/* SŁUPKI */}
      {showPillars && PillarMaterial && (
        <>
          <mesh
            name="Roofing2_PillarFront"
            geometry={pillarGeometryFront}
            material={PillarMaterial}
            position={[pillarFrontX, pillarFrontY, pillarCenterZ]}
            castShadow
            receiveShadow
          />
          <mesh
            name="Roofing2_PillarBack"
            geometry={pillarGeometryBack}
            material={PillarMaterial}
            position={[pillarBackX, pillarBackY, pillarCenterZ]}
            castShadow
            receiveShadow
          />
        </>
      )}

      {/* PŁYTA */}
      <Plyta
        length={baseLengthCm}
        width={extensionCm}
        anchoring={roofingExtension?.anchoring}
        position={[0, 0.05, slabCenterZ]}
      />
    </group>
  );
}


/* =========================================================================
 *  WRAPPER
 * ========================================================================= */

export default function Roofing(props) {
  const roofingExtension = useSelector(
    (state) => state.Construction.roofingExtension || {}
  );

  const sizeNum = Number(roofingExtension?.size || 0);
  const extensionCm = getExtensionCmFromSize(sizeNum);

  if (extensionCm <= 0) return null;

  const { activeConstruction } = props;

  const useRoofing2 =
    activeConstruction === 2 ||
    activeConstruction === 3 ||
    activeConstruction === 4;

  if (useRoofing2) {
    return (
      <RoofingType2
        {...props}
        roofingExtension={roofingExtension}
        extensionCm={extensionCm}
      />
    );
  }

  return (
    <RoofingBase
      {...props}
      roofingExtension={roofingExtension}
      extensionCm={extensionCm}
    />
  );
}

Roofing.propTypes = {
  activeConstruction: PropTypes.number.isRequired,
  textures: PropTypes.object,
  length: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  position: PropTypes.array,
};
